class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    const expanded = this.expandPartQuery(this.normalizePartQuery(this.getQuery()) || this.getQuery());
    if (expanded !== this.getQuery()) this.input.value = expanded;
    // Lane S: the results page lists products (filters apply to products only); pages and guides stay in the dropdown.
    if (!this.input.form.querySelector('input[name="type"]')) {
      const type = document.createElement('input');
      type.type = 'hidden';
      type.name = 'type';
      type.value = 'product';
      this.input.form.appendChild(type);
    }
  }

  // A.10: spaced and hyphenated cartridge spellings become the compact form first, so expandPartQuery can search
  // both spellings: "apc rbc 124", "APC RBC124", "apc-rbc-124" -> "apcrbc124"; "rbc 7", "rbc-7" -> "rbc7".
  // Returns null when the query is not one of these forms (callers keep the typed text as is).
  normalizePartQuery(q) {
    const term = (q || '').trim().toLowerCase();
    let m = term.match(/^apc[\s-]*rbc[\s-]*(\d+[a-z0-9-]*)$/);
    if (m && term !== 'apcrbc' + m[1]) return 'apcrbc' + m[1];
    m = term.match(/^rbc[\s-]+(\d+[a-z0-9-]*)$/);
    if (m) return 'rbc' + m[1];
    return null;
  }

  // A.10: one retry, never a loop, when a query with a space or hyphen finds no product: "<letters> <digits>"
  // loses its space ("jx 200" -> "jx200"), anything else loses its hyphens; a compact part number loses its last
  // character (Lane S).
  fallbackQuery(q) {
    if (/^[a-z]+\s+\d+$/i.test(q.trim())) return q.replace(/\s+/g, '');
    if (/-/.test(q)) return q.replace(/-/g, '');
    // Lane S: a part number with one character too many ("smt1500rm2ucx") retries without its last character.
    if (/^(?=.*\d)(?=.*[a-z])[a-z0-9]{6,}$/i.test(q.trim())) return q.trim().slice(0, -1);
    return '';
  }

  // APC prints its cartridge numbers as APCRBC###, while most listings say RBC###: search both spellings.
  // Lane S: window.upsbSearchExpand (snippets/upsb-search-expand.liquid) also adds the synonyms that measurably help
  // ("onduleur" -> UPS, refurbished <-> reconditionné); this fallback keeps the APC rule if it is missing.
  expandPartQuery(term) {
    if (typeof window.upsbSearchExpand === 'function') return window.upsbSearchExpand(term);
    const match = term.match(/^apc-?(rbc[a-z]?\d+[a-z0-9-]*)$/i);
    return match ? `${term} OR ${match[1]}` : term;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm, isFallback, typedTerm) {
    const typed = typedTerm || searchTerm;
    const fetchTerm = isFallback ? searchTerm : this.normalizePartQuery(searchTerm) || searchTerm;
    const queryKey = fetchTerm.replace(/ /g, '-').toLowerCase();
    const typedKey = (this.normalizePartQuery(typed) || typed).replace(/ /g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Lane S: up to 10 results of EACH kind (Shopify's default is 10 in total, so 5 query suggestions left 5 products);
    // the section shows at most 4 per product group, 4 collections, 3 pages.
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(this.expandPartQuery(fetchTerm))}&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        // A.10: no product row in the answer -> one fallback retry
        if (!isFallback && resultsMarkup.indexOf('predictive-search-option-product-') === -1) {
          const fb = this.fallbackQuery(fetchTerm);
          if (fb && fb !== fetchTerm) {
            this.getSearchResults(fb, true, typed);
            return;
          }
        }

        // Save bandwidth keeping the cache in all instances synced (a fallback answer also under the typed query)
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          if (isFallback) predictiveSearchInstance.cachedResults[typedKey] = resultsMarkup;
        });

        // A.10: requests are aborted only on form reset, not per keystroke, so a slow answer (the fallback adds a
        // round trip) could replace the results of a newer query: render only the query still in the box.
        if (typed !== this.searchTerm) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.updateTotal(this.searchTerm);
  }

  // Lane S: "See all results (58)": the count the results page will show, from sections/upsb-search-count.liquid.
  updateTotal(term) {
    const el = this.querySelector('[data-upsb-ps-total]');
    if (!el || !term) return;
    this.totals = this.totals || {};
    const q = this.expandPartQuery(this.normalizePartQuery(term) || term);
    const show = (n) => {
      if (term !== this.searchTerm || n === null || n === undefined || n === '') return;
      el.textContent = `(${n})`;
      el.hidden = false;
    };
    if (q in this.totals) return show(this.totals[q]);
    // window.routes has no search_url here; the predictive endpoint is "<locale root>search/suggest".
    const searchUrl = (routes.predictive_search_url || '/search/suggest').replace(/\/suggest$/, '');
    fetch(`${searchUrl}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last&section_id=upsb-search-count`)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const m = html.match(/data-upsb-count="(\d+)"/);
        this.totals[q] = m ? m[1] : null;
        show(this.totals[q]);
      })
      .catch(() => {});
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);